import React, {useRef, useState, useEffect} from 'react'
import {useParams, useSearchParams, useNavigate} from 'react-router-dom'
import axios from 'axios'
import moment from 'moment'
import Select from 'react-select'
import SignatureCanvas from 'react-signature-canvas'
import toaster from '../../../../Utils/toaster'
import {useAuth} from '../../../modules/auth'
import VosRatingPanel from './VosRatingPanel'
const API_URL = process.env.REACT_APP_API_URL
const VOS_SUPPORT_EMAIL = 'interpreting@ad-astrainc.com'

const HoursOptions = Array.from({length: 23}, (_, i) => ({
  value: i,
  label: String(i).padStart(2, '0'),
}))

const MinutesOptions = Array.from({length: 60}, (_, i) => ({
  value: i,
  label: String(i).padStart(2, '0'),
}))

function normalizeTravelHours(value: number | null | undefined): number {
  const n = Math.floor(Number(value ?? 0))
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.min(22, n)
}

function normalizeTravelMinutes(value: number | null | undefined): number {
  const n = Math.floor(Number(value ?? 0))
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.min(59, n)
}

type Mode = 'confirm' | 'dispute'

function isThirdPartyCommunicationType(communicationType?: string | null): boolean {
  return (communicationType ?? '').toLowerCase() === 'tpp'
}

type VosConfirmRequestBody = {
  appointmentId: number
  signature?: string
  token?: string
  signedVia?: number
  isDisputed: boolean
  startTime: string
  endTime: string
  isTimeChargeableForInterpreter?: boolean
  isTimeChargeableForClient?: boolean
  staffMemberName?: string
  staffMemberEmail?: string
  milage?: number
  isRoundTrip?: boolean
  travelTimeH?: number
  travelTimeM?: number
  isMileagePayableForInterpreter?: boolean
  isMileageChargeableForClient?: boolean
  isTravelTimePayableForInterpreter?: boolean
  isTravelTimeChargeableForClient?: boolean
  breakTimeMinutes?: number
}

function isVosTokenExpiredError(err: unknown): boolean {
  const response = (err as {response?: {status?: number; data?: {code?: string; Code?: string}}})?.response
  if (response?.status !== 401) return false
  const code = response.data?.code ?? response.data?.Code
  return code === 'VOS_TOKEN_EXPIRED'
}

export const VOSVerificationView: React.FC<{mode: Mode; standalone?: boolean}> = ({mode, standalone = false}) => {
  const {id} = useParams<{id: string}>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? undefined
  const disputeQuery = searchParams.get('dispute') === '1'
  const navigate = useNavigate()
  const {currentUser} = useAuth()
  const currentUserRef = useRef(currentUser)
  currentUserRef.current = currentUser
  const sigRef = useRef<SignatureCanvas>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [isDispute, setIsDispute] = useState(mode === 'dispute' || disputeQuery)
  const [signatureError, setSignatureError] = useState<string | null>(null)
  const [form, setForm] = useState<{
    code: number
    startDate: string
    endDate: string
    fK_AppointmentCode: number
    recordId?: string | null
    milage?: number | null
    isRoundTrip?: boolean
    travalTimeH?: number | null
    travalTimeM?: number | null
    isMileagePayableForInterpreter?: boolean | null
    isMileageChargeableForClient?: boolean | null
    isTravelTimePayableForInterpreter?: boolean | null
    isTravelTimeChargeableForClient?: boolean | null
    isTimeChargeableForClient?: boolean | null
    isTimeChargeableForInterpreter?: boolean | null
    staffMemberName?: string | null
    staffMemberEmail?: string | null
    breakTimeMinutes?: number | null
    status?: string | null
    communicationType?: string | null
  } | null>(null)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [ratingFinished, setRatingFinished] = useState(false)
  const [startDateTime, setStartDateTime] = useState('')
  const [endDateTime, setEndDateTime] = useState('')
  const [milage, setMilage] = useState<string>('0')
  const [travelTimeH, setTravelTimeH] = useState<number>(0)
  const [travelTimeM, setTravelTimeM] = useState<number>(0)
  const [breakTimeMinutes, setBreakTimeMinutes] = useState<number | ''>('')
  const [linkExpired, setLinkExpired] = useState(false)

  const appointmentId = id ? parseInt(id, 10) : 0
  const isThirdPartyPlatform = isThirdPartyCommunicationType(form?.communicationType)

  const formatDateTime = (value: string) => {
    if (!value) return ''
    const m = moment(value)
    if (!m.isValid()) return value
    return m.format('dddd, MM/DD/YYYY, hh:mm A')
  }

  useEffect(() => {
    if (currentUser?.result?.userType === 'INTERPRETER') return
    if (!appointmentId) return
    setLinkExpired(false)
    const url = token
      ? `${API_URL}/Appoinment/vos-form/${appointmentId}?token=${encodeURIComponent(token)}`
      : `${API_URL}/Appoinment/vos-form/${appointmentId}`
    axios
      .get(url, {withCredentials: true})
      .then((res) => {
        if (currentUserRef.current?.result?.userType === 'INTERPRETER') return
        const data = res.data
        const code = data?.code ?? data?.Code
        const startDate =
          data?.interpreterStartDate ?? data?.InterpreterStartDate ?? data?.startDate ?? data?.StartDate
        const endDate =
          data?.interpreterEndDate ?? data?.InterpreterEndDate ?? data?.endDate ?? data?.EndDate
        const fk = data?.fK_AppointmentCode ?? data?.FK_AppointmentCode
        const hasRequesterSubmission =
          data?.hasRequesterVOSSubmission ?? data?.HasRequesterVOSSubmission ?? false
        const interpretedMilage =
          data?.interpreterMilage ?? data?.InterpreterMilage ?? data?.milage ?? data?.Milage
        const interpretedRoundTrip =
          data?.interpreterIsRoundTrip ?? data?.InterpreterIsRoundTrip ?? data?.isRoundTrip ?? data?.IsRoundTrip
        const interpretedTravelTimeH =
          data?.interpreterTravelTimeH ?? data?.InterpreterTravelTimeH ?? data?.travalTimeH ?? data?.TravalTimeH
        const interpretedTravelTimeM =
          data?.interpreterTravelTimeM ?? data?.InterpreterTravelTimeM ?? data?.travalTimeM ?? data?.TravalTimeM
        const interpretedMileagePayable =
          data?.interpreterIsMileagePayableForInterpreter ??
          data?.InterpreterIsMileagePayableForInterpreter ??
          data?.isMileagePayableForInterpreter ??
          data?.IsMileagePayableForInterpreter
        const interpretedMileageChargeable =
          data?.interpreterIsMileageChargeableForClient ??
          data?.InterpreterIsMileageChargeableForClient ??
          data?.isMileageChargeableForClient ??
          data?.IsMileageChargeableForClient
        const interpretedTravelPayable =
          data?.interpreterIsTravelTimePayableForInterpreter ??
          data?.InterpreterIsTravelTimePayableForInterpreter ??
          data?.isTravelTimePayableForInterpreter ??
          data?.IsTravelTimePayableForInterpreter
        const interpretedTravelChargeable =
          data?.interpreterIsTravelTimeChargeableForClient ??
          data?.InterpreterIsTravelTimeChargeableForClient ??
          data?.isTravelTimeChargeableForClient ??
          data?.IsTravelTimeChargeableForClient
        const interpretedTimeChargeableForClient =
          data?.interpreterIsTimeChargeableForClient ??
          data?.InterpreterIsTimeChargeableForClient ??
          data?.isTimeChargeableForClient ??
          data?.IsTimeChargeableForClient
        const interpretedTimeChargeableForInterpreter =
          data?.interpreterIsTimeChargeableForInterpreter ??
          data?.InterpreterIsTimeChargeableForInterpreter ??
          data?.isTimeChargeableForInterpreter ??
          data?.IsTimeChargeableForInterpreter
        const interpretedBreakTimeMinutes =
          data?.interpreterBreakTimeMinutes ??
          data?.InterpreterBreakTimeMinutes ??
          data?.breakTimeMinutes ??
          data?.BreakTimeMinutes
        const staffMemberName = data?.staffMemberName ?? data?.StaffMemberName
        const staffMemberEmail = data?.staffMemberEmail ?? data?.StaffMemberEmail
        const status = data?.status ?? data?.Status
        const communicationType = data?.communicationType ?? data?.CommunicationType ?? null
        const recordId = data?.recordId ?? data?.RecordId ?? null
        if (hasRequesterSubmission) {
          setAlreadySubmitted(true)
        }
        if (code != null && fk != null) {
          setForm({
            code,
            startDate: startDate ?? '',
            endDate: endDate ?? '',
            fK_AppointmentCode: fk,
            recordId,
            milage: interpretedMilage ?? null,
            isRoundTrip: !!interpretedRoundTrip,
            travalTimeH: interpretedTravelTimeH ?? null,
            travalTimeM: interpretedTravelTimeM ?? null,
            isMileagePayableForInterpreter: interpretedMileagePayable ?? null,
            isMileageChargeableForClient: interpretedMileageChargeable ?? null,
            isTravelTimePayableForInterpreter: interpretedTravelPayable ?? null,
            isTravelTimeChargeableForClient: interpretedTravelChargeable ?? null,
            isTimeChargeableForClient: interpretedTimeChargeableForClient ?? null,
            isTimeChargeableForInterpreter: interpretedTimeChargeableForInterpreter ?? null,
            staffMemberName: staffMemberName ?? null,
            staffMemberEmail: staffMemberEmail ?? null,
            breakTimeMinutes:
              interpretedBreakTimeMinutes != null
                ? Number(interpretedBreakTimeMinutes)
                : null,
            status: status ?? null,
            communicationType,
          })
          if (startDate) {
            setStartDateTime(moment(startDate).format('YYYY-MM-DDTHH:mm'))
          }
          if (endDate) {
            setEndDateTime(moment(endDate).format('YYYY-MM-DDTHH:mm'))
          }
          setMilage(String(interpretedMilage ?? 0))
          setTravelTimeH(normalizeTravelHours(interpretedTravelTimeH))
          setTravelTimeM(normalizeTravelMinutes(interpretedTravelTimeM))
          setBreakTimeMinutes(
            interpretedBreakTimeMinutes != null ? Number(interpretedBreakTimeMinutes) : ''
          )
        }
      })
      .catch((err) => {
        if (token && isVosTokenExpiredError(err)) {
          setLinkExpired(true)
          return
        }
        toaster('error', 'Failed to load VOS form')
      })
      .finally(() => setLoading(false))
  }, [appointmentId, token, mode, currentUser?.result?.userType])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (currentUser?.result?.userType === 'INTERPRETER') return
    if (appointmentId <= 0) return
    if (!form) return
    if (!startDateTime || !endDateTime) {
      toaster('error', 'Start and end time are required.')
      return
    }
    const sigInstance: any = sigRef.current as any
    if (!sigInstance || (typeof sigInstance.isEmpty === 'function' && sigInstance.isEmpty())) {
      setSignatureError('Signature is required.')
      toaster('error', 'Signature is required.')
      return
    }
    const trimmed = sigInstance.getTrimmedCanvas()
    const dataUrl = trimmed ? trimmed.toDataURL('image/png') : null
    if (isDispute) {
      const startDate = new Date(startDateTime)
      const endDate = new Date(endDateTime)
      if (endDate <= startDate) {
        toaster('error', 'End time must be after start time.')
        return
      }
    }
    setSubmitting(true)
    try {
      const body: VosConfirmRequestBody = {
        appointmentId,
        token: token || undefined,
        signedVia: 1, // Email/Web
        isDisputed: isDispute,
        startTime: moment(startDateTime, 'YYYY-MM-DDTHH:mm', true).format('YYYY-MM-DDTHH:mm:ss'),
        endTime: moment(endDateTime, 'YYYY-MM-DDTHH:mm', true).format('YYYY-MM-DDTHH:mm:ss'),
        isTimeChargeableForInterpreter: form.isTimeChargeableForInterpreter ?? true,
        isTimeChargeableForClient: form.isTimeChargeableForClient ?? true,
      }
      if (!isThirdPartyPlatform) {
        body.milage = milage.trim() === '' ? 0 : Number(milage)
        body.isRoundTrip = form.isRoundTrip ?? false
        const normalizedTravelH = normalizeTravelHours(travelTimeH)
        const normalizedTravelM = normalizeTravelMinutes(travelTimeM)
        body.travelTimeH = normalizedTravelH
        body.travelTimeM = normalizedTravelM
        body.isMileagePayableForInterpreter = form.isMileagePayableForInterpreter ?? true
        body.isMileageChargeableForClient = form.isMileageChargeableForClient ?? true
        body.isTravelTimePayableForInterpreter = form.isTravelTimePayableForInterpreter ?? true
        body.isTravelTimeChargeableForClient = form.isTravelTimeChargeableForClient ?? true
      }
      if (breakTimeMinutes !== '') {
        body.breakTimeMinutes = Number(breakTimeMinutes)
      }
      if (token) {
        const sn = form.staffMemberName?.trim()
        const se = form.staffMemberEmail?.trim()
        if (sn) body.staffMemberName = sn
        if (se) body.staffMemberEmail = se
      }
      if (dataUrl) body.signature = dataUrl
      await axios.post(`${API_URL}/Appoinment/vos-confirm`, body, {withCredentials: true})
      toaster('success', isDispute ? 'VOS dispute submitted successfully' : 'VOS confirmed successfully')
      setSubmitted(true)
    } catch (err: unknown) {
      if (token && isVosTokenExpiredError(err)) {
        setLinkExpired(true)
        return
      }
      const message = (err as {response?: {data?: {text?: string}}})?.response?.data?.text
      toaster('error', message ?? 'Request failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (currentUser?.result?.userType === 'INTERPRETER') {
    return (
      <div className='card card-flush p-4 p-lg-5'>
        <h3 className='mb-3'>VOS Meeting Verification</h3>
        <p className='text-gray-700 mb-4'>
          This form can only be completed by a requester or staff member.
        </p>
        <button
          type='button'
          className='btn btn-light'
          onClick={() => navigate('/')}
        >
          Go back
        </button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-vh-50'>
        <div className='spinner-border text-primary' role='status' />
      </div>
    )
  }

  if (linkExpired) {
    return (
      <div className='card card-flush p-4 p-lg-5'>
        <h3 className='mb-3'>VOS Meeting Verification</h3>
        <p className='text-gray-700 mb-3'>
          The link for this form submission has expired. Please contact support for assistance.
        </p>
        <p className='mb-4'>
          <a href={`mailto:${VOS_SUPPORT_EMAIL}`}>{VOS_SUPPORT_EMAIL}</a>
        </p>
        {appointmentId > 0 && (
          <p className='text-gray-600 mb-4'>
            Appointment ID: <strong>{appointmentId}</strong>
          </p>
        )}
      </div>
    )
  }

  if (!form) {
    return (
      <div className='p-5'>
        <p className='text-danger'>No VOS submission found for this appointment.</p>
      </div>
    )
  }

  if (alreadySubmitted) {
    return (
      <div className='card card-flush p-4 p-lg-5'>
        <h3 className='mb-3'>VOS Meeting Verification</h3>
        <p className='text-success mb-2'>This VOS form has already been confirmed or disputed.</p>
        <p className='text-gray-600 mb-4'>
          Appointment ID: <strong>{form.fK_AppointmentCode}</strong>
          <br />
          Record ID: <strong>{form.recordId?.trim() || 'N/A'}</strong>
        </p>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className='card card-flush p-4 p-lg-5'>
        <h3 className='mb-3'>VOS Meeting Verification</h3>
        <p className='text-success mb-2'>Form submitted.</p>
        <p className='text-gray-600 mb-4'>
          Your confirmation{isDispute ? ' or dispute' : ''} has been recorded for appointment{' '}
          <strong>{form.fK_AppointmentCode}</strong>
          {'. '}
          Record ID: <strong>{form.recordId?.trim() || 'N/A'}</strong>.
        </p>
        {!ratingFinished ? (
          <VosRatingPanel
            appointmentId={form.fK_AppointmentCode}
            token={token}
            staffMemberName={form.staffMemberName}
            staffMemberEmail={form.staffMemberEmail}
            onSkipped={() => setRatingFinished(true)}
            onSubmitted={() => setRatingFinished(true)}
          />
        ) : (
          <p className='text-muted mb-0'>You can close this page.</p>
        )}
      </div>
    )
  }

  return (
    <div className='card card-flush p-4 p-lg-5'>
      <h3 className='mb-3'>Confirm or dispute VOS Meeting Summary</h3>
      <div className='text-gray-600 mb-4'>
        <h5 className='mb-2' style={{textDecoration: 'underline'}}>Appointment Details</h5>
        <p className='mb-2'>
          Appointment ID: <strong>{form.fK_AppointmentCode}</strong>
          <br />
          Record ID: <strong>{form.recordId?.trim() || 'N/A'}</strong>
          <br />
          Start: {formatDateTime(form.startDate)} — End: {formatDateTime(form.endDate)}
        </p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className='form-check mb-4'>
          <input
            id='vos-dispute-checkbox'
            type='checkbox'
            className='form-check-input'
            checked={isDispute}
            onChange={(e) => {
              const checked = e.target.checked
              setIsDispute(checked)
              if (form) {
                if (form.startDate) {
                  setStartDateTime(moment(form.startDate).format('YYYY-MM-DDTHH:mm'))
                }
                if (form.endDate) {
                  setEndDateTime(moment(form.endDate).format('YYYY-MM-DDTHH:mm'))
                }
                if (!isThirdPartyPlatform) {
                  setMilage(String(form.milage ?? 0))
                  setTravelTimeH(normalizeTravelHours(form.travalTimeH))
                  setTravelTimeM(normalizeTravelMinutes(form.travalTimeM))
                  setBreakTimeMinutes(
                    form.breakTimeMinutes != null ? Number(form.breakTimeMinutes) : ''
                  )
                }
              }
            }}
          />
          <label className='form-check-label' htmlFor='vos-dispute-checkbox'>
            I dispute this VOS summary (edit any field below)
          </label>
        </div>
        <div className='row g-3 mb-4'>
          <div className='col-12 col-md-6'>
            <label className='form-label required'>Start date & time</label>
            <input
              type='datetime-local'
              className='form-control'
              value={startDateTime}
              onChange={(e) => setStartDateTime(e.target.value)}
              required
              readOnly={!isDispute}
            />
          </div>
          <div className='col-12 col-md-6'>
            <label className='form-label required'>End date & time</label>
            <input
              type='datetime-local'
              className='form-control'
              value={endDateTime}
              onChange={(e) => setEndDateTime(e.target.value)}
              required
              readOnly={!isDispute}
            />
          </div>
          {!isThirdPartyPlatform && (
            <div className='col-12 col-md-6'>
              <label className='form-label'>Break Time (mins)</label>
              <input
                type='number'
                className='form-control'
                value={breakTimeMinutes}
                onChange={(e) => {
                  const val = e.target.value
                  setBreakTimeMinutes(val === '' ? '' : Number(val))
                }}
                readOnly={!isDispute}
                min={0}
                step={1}
              />
            </div>
          )}
          {!isThirdPartyPlatform && (
            <>
              <div className='col-12 col-md-6'>
                <label className='form-label'>Mileage</label>
                <input
                  type='number'
                  className='form-control'
                  value={milage}
                  onChange={(e) => setMilage(e.target.value)}
                  readOnly={!isDispute}
                  min={0}
                  step='0.01'
                />
              </div>
              <div className='col-12'>
                <label className='form-label'>Travel Time</label>
                <div className='input-group input-group-sm'>
                  <div className='d-flex w-auto' style={{flex: 1}}>
                    <Select
                      className='react-select-styled react-select-solid react-select-sm flex-grow-1'
                      classNamePrefix='react-select'
                      options={HoursOptions}
                      placeholder='Select Hours'
                      styles={{
                        control: (provided: Record<string, unknown>) => ({
                          ...provided,
                          width: '100%',
                          border: '1px solid #e4e6ef',
                          borderRadius: '4px 0 0 4px',
                        }),
                      }}
                      value={{
                        value: travelTimeH,
                        label:
                          HoursOptions.find((x) => x.value === travelTimeH)?.label ??
                          'Select Hours',
                      }}
                      onChange={(e: {value?: number} | null) => {
                        if (isDispute && e?.value != null) {
                          setTravelTimeH(normalizeTravelHours(e.value))
                        }
                      }}
                      isDisabled={!isDispute}
                    />
                  </div>
                  <span className='input-group-text' style={{flex: 1}}>
                    Hours
                  </span>
                  <div className='d-flex w-auto' style={{flex: 1}}>
                    <Select
                      className='react-select-styled react-select-solid react-select-sm flex-grow-1'
                      classNamePrefix='react-select'
                      options={MinutesOptions}
                      placeholder='Select Minutes'
                      styles={{
                        control: (provided: Record<string, unknown>) => ({
                          ...provided,
                          width: '100%',
                          border: '1px solid #e4e6ef',
                          borderRadius: '0',
                        }),
                      }}
                      value={{
                        value: travelTimeM,
                        label:
                          MinutesOptions.find((x) => x.value === travelTimeM)?.label ??
                          'Select Minutes',
                      }}
                      onChange={(e: {value?: number} | null) => {
                        if (isDispute && e?.value != null) {
                          setTravelTimeM(normalizeTravelMinutes(e.value))
                        }
                      }}
                      isDisabled={!isDispute}
                    />
                  </div>
                  <span className='input-group-text' style={{flex: 1}}>
                    Minutes
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
        <div className='mb-4'>
          <label className='form-label required'>Your signature</label>
          <div
            className='border rounded bg-light'
            style={{width: '100%', maxWidth: 400, height: 180}}
          >
            <SignatureCanvas
              ref={sigRef}
              {...({onEnd: () => setSignatureError(null)} as any)}
              canvasProps={{
                className: 'w-100 h-100',
                style: {width: '100%', height: '100%'},
              }}
              backgroundColor='rgb(248, 249, 250)'
              penColor='black'
            />
          </div>
          <button
            type='button'
            className='btn btn-sm btn-light mt-1'
            onClick={() => sigRef.current?.clear()}
          >
            Clear signature
          </button>
          {signatureError && (
            <div className='fv-plugins-message-container mt-1'>
              <div className='fv-help-block'>
                <span role='alert' className='text-danger'>{signatureError}</span>
              </div>
            </div>
          )}
        </div>
        <div className='d-flex gap-2'>
          <button type='submit' className='btn btn-primary' disabled={submitting}>
            {submitting ? 'Submitting...' : isDispute ? 'Submit dispute' : 'Confirm'}
          </button>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => (standalone ? window.history.back() : navigate(`/appointmentViewer/${appointmentId}`))}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
